import React, {FC} from 'react';
import styled from 'styled-components';

import {isDefined} from '../../../../../../libs/shared/core/src/helpers/typescript/typeHelpers';
import {AttachmentVisualContentTypesEnum} from '../../../../../../libs/shared/core/src/models/api/attachmentModel';
import {PaletteColorsEnum} from '../../../../../../libs/shared/core/src/models/api/paletteModel';
import {ellipsis} from '../../../../../../libs/shared/ui/src/styles/mixins';
import {buildCellsDisplaySummary} from '../../../../../../srcCommon/inboxes/cells/cellsSummaryHelpers';
import {HighlightModel, SearchActivityTypesEnum} from '../../../../../models/api/conversationHighlightModel';
import {MutationProofText} from '../../../../common/text/mutationProofText';
import {useCompactP2Context} from '../../compactP2Context';
import {Highlight} from '../../header/search/highlight/highlight';
import {CellDraftIcon} from './cellDraftIcon';
import {CellRepliedIcon} from './cellRepliedIcon';
import {CellSummaryAttachmentIcon} from './cellSummaryAttachmentIcon';
import {CellSummaryPlaceholder} from './cellSummaryPlaceholder';

/*
 * Props.
 */

interface CellSummaryProps {
  isSelected?: boolean;
  isReply?: boolean;
  summary?: string;
  type?: SearchActivityTypesEnum;
  attachmentContentType?: AttachmentVisualContentTypesEnum;
  highlight?: HighlightModel;
}

/*
 * Style.
 */

interface SummaryStyleProps {
  $isSelected?: boolean;
  $hasHighlight?: boolean;
}

const StyledSummaryDiv = styled.div<SummaryStyleProps>`
  grid-area: bottom-line;
  /** If we have a highlight, rely on <Highlight /> to use the correct colors. */
  color: ${(p) => (p.$isSelected && !p.$hasHighlight ? p.theme.invertedSubtext : p.theme.greys.shade70)};

  padding: 3px;
  margin: -3px;
  ${ellipsis()};
`;

/*
 * Component.
 */

export const CellSummary: FC<CellSummaryProps> = (props) => {
  const {isCompact} = useCompactP2Context();

  return (
    <>
      {maybeRenderIcon(props, isCompact)}
      <StyledSummaryDiv $isSelected={Boolean(props.isSelected)} $hasHighlight={isDefined(props.highlight)}>
        {renderValue(props.highlight, props.summary, props.isSelected, isCompact)}
      </StyledSummaryDiv>
    </>
  );
};

function maybeRenderIcon(props: CellSummaryProps, isCompact: boolean) {
  if (props.isReply) {
    return <CellRepliedIcon isSelected={props.isSelected} />;
  }

  if (props.type === SearchActivityTypesEnum.ATTACHMENT) {
    return <CellSummaryAttachmentIcon contentType={props.attachmentContentType} />;
  }

  if (props.type === SearchActivityTypesEnum.DRAFT && !isCompact) {
    return (
      <CellDraftIcon
        isSelected={props.isSelected}
        iconName="draftFilledSmall"
        color={PaletteColorsEnum.BLUE}
      />
    );
  }

  return null;
}

function renderValue(
  highlight?: HighlightModel,
  summary?: string,
  isSelected?: boolean,
  isCompact?: boolean,
) {
  // If a highlight was provided, use it.
  if (highlight) {
    return (
      <MutationProofText>
        <Highlight
          {...highlight}
          backgroundColor={PaletteColorsEnum.YELLOW}
          isParentSelected={isSelected}
          hideWhitespaceOnly={isCompact}
        />
      </MutationProofText>
    );
  }

  // If a summary was provided, use it.
  const displaySummary = buildCellsDisplaySummary(summary);
  if (displaySummary) {
    return <MutationProofText>{displaySummary}</MutationProofText>;
  }

  // Otherwise, render a placeholder.
  return <CellSummaryPlaceholder />;
}
